import {
  Alert,
  App,
  Button,
  Input,
  Modal,
  Progress,
  Select,
  Space,
  Typography,
  Upload,
  theme,
} from 'antd'
import {
  CloseOutlined,
  PaperClipOutlined,
  SendOutlined,
  StarOutlined,
} from '@ant-design/icons'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Functions } from 'firebase/functions'
import { useTranslation } from 'react-i18next'
import { useStorage, useUser } from 'reactfire'
import { api } from '../../lib/api'
import { MAX_OUTBOUND_ATTACHMENTS } from '../../lib/shared/chatwootAttachments'
import {
  deleteChatwootAttachments,
  stageChatwootAttachments,
  type StagingProgress,
} from './attachmentUploads'
import { useOutboundAttachments } from './useOutboundAttachments'
import {
  clearNewConversationDraft,
  loadNewConversationDraft,
  saveDraft,
  saveNewConversationDraft,
} from './composerDrafts'
import {
  clearPendingAttachments,
  composerKey,
  movePendingAttachments,
  newConversationKey,
} from './composerOutbox'
import { usePendingAttachments } from './usePendingAttachments'
import {
  errorResponse,
  getLocalizedErrorMessage,
} from '../../lib/errorResponse'
import {
  useAttachmentDropzone,
  dropzoneHighlightStyle,
} from './useAttachmentDropzone'
import { AttachmentDropOverlay } from './AttachmentDropOverlay'
import { pickDefaultInbox } from './utils'
import { formatCompanyPickerLabel } from './companyFilter'
import { useChatwootContacts } from './hooks/useChatwootContacts'
import { useChatwootInboxes } from './hooks/useChatwootInboxes'
import { useResolveContactCompanies } from './hooks/useResolveContactCompanies'
import { useChatwootOperatorSignature } from './hooks/useChatwootOperatorSignature'
import { composeReplyWithDraft, hasSignature } from './replySignature'
import { ChatwootCleoComposeModal } from './ChatwootCleoComposeModal'
import { useChatwootCleoCompose } from './hooks/useChatwootCleoCompose'
import {
  ChatwootReplyRecipients,
  type ReplyRecipients,
} from './ChatwootReplyRecipients'
import {
  isLikelyEmailAddress,
  normalizeRecipients,
} from '../../lib/shared/chatwootRecipients'
import type {
  ChatwootContactsResponse,
  ChatwootInboxesResponse,
  ChatwootStartConversationResponse,
} from './types'

const { TextArea } = Input

// Lightweight check to decide when a typed value should be offered as a "send to
// this email" choice. The resolver soft-fails invalid addresses; this only
// gates the UI affordance.

// The conversation has no id yet, so staged files live under this segment in the
// operator's own outbound folder; the backend only checks the folder is the
// caller's, not the segment value. Mirrors the customer new-conversation form.
const NEW_CONVERSATION_STAGE_ID = 'new'

// An empty field is omitted rather than sent as [], so the request carries only
// what the operator actually set.
function buildCopyRecipients(recipients: ReplyRecipients) {
  const cc = normalizeRecipients(recipients.cc)
  const bcc = normalizeRecipients(recipients.bcc)
  return {
    ...(cc.length > 0 ? { cc } : {}),
    ...(bcc.length > 0 ? { bcc } : {}),
  }
}

interface ChatwootNewConversationProps {
  functions: Functions
  companyUid?: string | null
  open: boolean
  onClose: () => void
  // Called with the new conversation id once it (and its first message) are
  // created, so the inbox can select and open the thread.
  onCreated: (conversationId: string) => void
}

// Operator-initiated conversation, mirroring Chatwoot's Contacts -> New
// conversation flow: pick a target inbox, search for a contact, write the first
// message. The body is mounted only while the modal is open so the contact and
// inbox queries don't run (and reset cleanly) in the background.
export function ChatwootNewConversation({
  functions,
  companyUid,
  open,
  onClose,
  onCreated,
}: ChatwootNewConversationProps) {
  const { t } = useTranslation()

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      destroyOnClose
      title={t('frontend:chatwoot.newConversation.title')}
    >
      {open && (
        <NewConversationBody
          functions={functions}
          companyUid={companyUid}
          onClose={onClose}
          onCreated={onCreated}
        />
      )}
    </Modal>
  )
}

function NewConversationBody({
  functions,
  companyUid,
  onClose,
  onCreated,
}: Omit<ChatwootNewConversationProps, 'open'>) {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const { token } = theme.useToken()
  const storage = useStorage()
  const { data: user } = useUser()
  const { attachments, onSelect, remove, replace } = useOutboundAttachments()

  const [inboxId, setInboxId] = useState<string | undefined>()
  const [contactId, setContactId] = useState<string | undefined>()
  // Captured on pick: the contact list follows the search box, so the picked
  // contact is not necessarily in it later. Only Cleo reads it, for the greeting.
  const [contactName, setContactName] = useState<string | null>(null)
  // Cleo writing the first message (CL-2217). Nothing exists in Chatwoot yet,
  // so it drafts from the advisor's request, the company and the recipient.
  const cleoCompose = useChatwootCleoCompose(functions)
  const [composeOpen, setComposeOpen] = useState(false)
  // Subject, body and any typed Cc/Bcc are seeded from — and written back to — a
  // persisted draft, so it survives a modal close, reload, or failed send. The
  // contact and inbox are deliberately not persisted; re-picking them is cheap,
  // and a stale primary recipient is worse than no recipient.
  const [subject, setSubject] = useState(
    () => loadNewConversationDraft().subject
  )
  const [content, setContent] = useState(
    () => loadNewConversationDraft().content
  )
  // `to` stays empty: the recipient is the contact picked above.
  const [recipients, setRecipients] = useState<ReplyRecipients>(() => {
    const draft = loadNewConversationDraft()
    return { to: [], cc: draft.cc, bcc: draft.bcc }
  })
  const [submitting, setSubmitting] = useState(false)
  // Progress of the in-flight staging upload; null when nothing is uploading.
  const [uploadProgress, setUploadProgress] = useState<StagingProgress | null>(
    null
  )
  const sendAbortRef = useRef<AbortController | null>(null)

  // Persist on every edit, so the draft survives an unmount we never see coming
  // (modal close, navigation, reload).
  function updateSubject(next: string) {
    setSubject(next)
    saveNewConversationDraft({
      subject: next,
      content,
      cc: recipients.cc,
      bcc: recipients.bcc,
    })
  }

  function updateContent(next: string) {
    setContent(next)
    saveNewConversationDraft({
      subject,
      content: next,
      cc: recipients.cc,
      bcc: recipients.bcc,
    })
  }

  function updateRecipients(next: ReplyRecipients) {
    setRecipients(next)
    saveNewConversationDraft({ subject, content, cc: next.cc, bcc: next.bcc })
  }

  // Restore (and keep persisting) attachments picked here but never sent.
  usePendingAttachments({
    key: newConversationKey,
    attachments,
    replace,
  })
  // Accept files dropped anywhere on the modal body, through the same validated
  // picker as the paperclip button — ignored mid-submit, matching the paperclip
  // Upload button (disabled while submitting) and the other composers.
  const { isDraggingOver, dropzoneProps } = useAttachmentDropzone({
    onSelect,
    disabled: submitting,
  })
  // Bumped by the retry action to force the inbox/contact callables to refetch
  // after a failed load.
  const [reloadKey, setReloadKey] = useState(0)

  // Contact search is server-side; debounce the typed query so a callable isn't
  // fired on every keystroke (mirrors the inbox search debounce).
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  useEffect(() => {
    const handle = window.setTimeout(() => setSearch(searchInput.trim()), 300)
    return () => window.clearTimeout(handle)
  }, [searchInput])

  const { data: inboxesData, status: inboxesStatus } = useChatwootInboxes(
    companyUid,
    reloadKey
  )
  const inboxes = useMemo(() => {
    const response = inboxesData as ChatwootInboxesResponse | undefined
    return response?.inboxes || response?.result?.inboxes || []
  }, [inboxesData])

  // Pre-select the operator's own email inbox once both the inbox list and the
  // signed-in operator are known, so the common case is one tap and lands in
  // their own inbox rather than whichever inbox Chatwoot returns first.
  // Waiting for `user` to resolve (it is undefined only while auth is loading)
  // avoids locking in the first inbox before the operator's email — used to
  // match their own inbox — is available; the operator can still switch.
  useEffect(() => {
    if (inboxId || inboxes.length === 0 || user === undefined) {
      return
    }
    const defaultInbox = pickDefaultInbox(inboxes, user?.email)
    if (defaultInbox) {
      setInboxId(defaultInbox.id)
    }
  }, [inboxId, inboxes, user])

  // Only email conversations carry a signature; chat/widget replies don't. The
  // Chatwoot channel type is the class name (e.g. "Channel::Email"), so match
  // loosely to stay robust to variants.
  const isEmailInbox = useMemo(() => {
    const selected = inboxes.find((inbox) => inbox.id === inboxId)
    return (selected?.channelType ?? '').toLowerCase().includes('email')
  }, [inboxes, inboxId])

  const { signature, status: signatureStatus } =
    useChatwootOperatorSignature(companyUid)
  // Blocks send until the lookup settles, or a fast typist on a slow connection
  // sends unsigned. A failure unblocks rather than stranding the operator.
  const signatureBlocksSend = isEmailInbox && signatureStatus === 'loading'

  const { data: contactsData, status: contactsStatus } = useChatwootContacts(
    search,
    1,
    companyUid,
    reloadKey
  )
  const contacts = useMemo(() => {
    const response = contactsData as ChatwootContactsResponse | undefined
    return response?.contacts || response?.result?.contacts || []
  }, [contactsData])

  const {
    stampCompanyUid,
    selectedCompanyUid,
    setSelectedCompanyUid,
    candidates,
    loading: companyResolutionLoading,
    failed: companyResolutionFailed,
    retry: retryCompanyResolution,
    needsCompanyPick,
  } = useResolveContactCompanies(functions, companyUid, contactId)

  const companyPickerOptions = useMemo(
    () =>
      candidates.map((candidate) => ({
        value: candidate.uid,
        label: formatCompanyPickerLabel(candidate, {
          unknown: t('frontend:chatwoot.filter.unknownCompany'),
          archived: t('frontend:chatwoot.filter.archivedCompany'),
        }),
      })),
    [candidates, t]
  )

  const contactOptions = useMemo(
    () =>
      contacts.map((contact) => {
        const name = contact.name?.trim()
        const detail = contact.email || contact.phoneNumber
        const label =
          name && detail
            ? `${name} · ${detail}`
            : name ||
              detail ||
              t('frontend:chatwoot.newConversation.unnamedContact')
        return { value: contact.id, label }
      }),
    [contacts, t]
  )

  // Free email entry: when the operator types an address that isn't already a
  // contact, offer a "send to <email>" option. Its value is prefixed with
  // `email:` so handleSubmit can tell it apart from a real contact id and hand
  // the backend an email to find-or-create instead. Contact ids are numeric, so
  // the prefix never collides with a real selection.
  const trimmedSearch = searchInput.trim()
  const typedEmail = isLikelyEmailAddress(trimmedSearch) ? trimmedSearch : null
  const selectedEmail = contactId?.startsWith('email:')
    ? contactId.slice('email:'.length)
    : null
  const contactSelectOptions = useMemo(() => {
    const emails: { value: string; label: string }[] = []
    const pushEmail = (address: string) => {
      const value = `email:${address}`
      if (emails.some((option) => option.value === value)) return
      emails.push({
        value,
        label: t('frontend:chatwoot.newConversation.sendToEmail', {
          email: address,
        }),
      })
    }
    if (
      typedEmail &&
      !contacts.some(
        (contact) =>
          contact.email &&
          contact.email.toLowerCase() === typedEmail.toLowerCase()
      )
    ) {
      pushEmail(typedEmail)
    }
    // Keep the selected email present even after the search text changes, so the
    // Select can still render its label for the current value.
    if (selectedEmail) {
      pushEmail(selectedEmail)
    }
    return [...emails, ...contactOptions]
  }, [contactOptions, contacts, typedEmail, selectedEmail, t])

  // Distinguish a genuine load failure from an empty result: on failure the
  // operator needs an explanation and a retry, not a silent empty picker.
  const inboxesFailed = inboxesStatus === 'error'
  const contactsFailed = contactsStatus === 'error'
  const loadFailed = inboxesFailed || contactsFailed

  // An attachment-only first message is a valid send (the backend enforces
  // "content or attachments" too).
  const hasTypedMessage = Boolean(content.trim())
  // A malformed copy recipient blocks the send here, which the reply composer
  // deliberately does not do. Chatwoot rejects the whole message on a bad
  // address, and this is the one path where that leaves something behind: the
  // conversation is created before the first message is posted, so the operator
  // is left with an empty thread and a second attempt would create a second
  // conversation. A reply just fails and can be retried in place.
  const hasInvalidCopyRecipient = [...recipients.cc, ...recipients.bcc].some(
    (address) => !isLikelyEmailAddress(address)
  )
  // Company assignment is not a gate: resolution stamps the thread when it can,
  // but an unresolved or ambiguous company must not stop an operator answering.
  const canSubmit = Boolean(
    inboxId &&
    contactId &&
    !signatureBlocksSend &&
    (hasTypedMessage || attachments.length > 0) &&
    !hasInvalidCopyRecipient &&
    !loadFailed
  )

  // Cleo needs to know who the message is for: the recipient, the company it
  // concerns (still resolving, or awaiting the operator's pick, means not yet)
  // and the inbox, which decides whether it is an email or a chat message.
  const canCompose = Boolean(
    inboxId &&
    contactId &&
    !companyResolutionLoading &&
    !(needsCompanyPick && !selectedCompanyUid) &&
    !loadFailed &&
    !submitting
  )

  // The draft lands in the message field for the advisor to review — it is
  // never sent from here. The dialog stays open while Cleo writes and on
  // failure, so the request can be retried without retyping it.
  async function onComposeSubmit(instructions: string) {
    const text = await cleoCompose.compose({
      companyUid: stampCompanyUid,
      instructions,
      recipientName: contactName,
      channel: isEmailInbox ? 'email' : 'chat',
    })
    if (!text) {
      return
    }
    setComposeOpen(false)
    updateContent(composeReplyWithDraft(content, text))
  }

  async function handleSubmit() {
    if (!canSubmit || !inboxId || !contactId) {
      return
    }
    // Staging writes to the caller's own outbound folder, so the send can't be
    // built without a signed-in user to key that path on.
    if (attachments.length > 0 && !user) {
      message.error(t('frontend:chatwoot.newConversation.failed'))
      return
    }
    setSubmitting(true)
    const abortController = new AbortController()
    sendAbortRef.current = abortController

    let stagedPaths: string[] = []
    try {
      // Inner `finally` so the progress bar — and with it the cancel button,
      // which only abort-reaches the staging step — cannot survive into the
      // start_conversation call below. Same reasoning as the reply composer.
      try {
        stagedPaths = user
          ? await stageChatwootAttachments({
              storage,
              uid: user.uid,
              conversationId: NEW_CONVERSATION_STAGE_ID,
              files: attachments,
              onProgress: setUploadProgress,
              signal: abortController.signal,
            })
          : []
      } finally {
        setUploadProgress(null)
      }
      // An `email:` sentinel value means the operator typed a fresh address;
      // hand the backend the email (find-or-create) instead of a contact id.
      const recipient = contactId.startsWith('email:')
        ? { email: contactId.slice('email:'.length) }
        : { contactId }
      const result = (await api(functions, 'chatwoot/start_conversation', {
        companyUid: stampCompanyUid || undefined,
        inboxId,
        ...recipient,
        // Only email threads carry a subject; the field isn't shown (or sent)
        // for chat/widget inboxes, which have no subject line.
        ...(isEmailInbox && subject.trim() ? { subject: subject.trim() } : {}),
        ...(isEmailInbox ? buildCopyRecipients(recipients) : {}),
        content: content.trim(),
        ...(isEmailInbox && hasSignature(signature)
          ? { signature: signature as string }
          : {}),
        ...(stagedPaths.length > 0 ? { attachments: stagedPaths } : {}),
      })) as ChatwootStartConversationResponse
      const conversation = result?.conversation || result?.result?.conversation
      if (!conversation?.id) {
        throw new Error('Missing conversation id')
      }
      const messageSent =
        result?.messageSent ?? result?.result?.messageSent ?? true
      if (messageSent) {
        // Delivered — the recovery copies have done their job.
        clearNewConversationDraft()
        await clearPendingAttachments(newConversationKey)
        message.success(t('frontend:chatwoot.newConversation.created'))
      } else {
        // The conversation exists but the first message didn't send; open it so
        // the operator can resend from the composer instead of starting over.
        // Hand the message and its attachment over to that thread's composer so
        // "resend from the thread" doesn't mean retyping it.
        saveDraft(conversation.id, 'reply', content.trim())
        await movePendingAttachments(
          newConversationKey,
          composerKey(conversation.id, 'reply')
        )
        clearNewConversationDraft()
        message.warning(t('frontend:chatwoot.newConversation.messageFailed'))
      }
      onCreated(conversation.id)
    } catch (error) {
      // The backend deletes staged files after forwarding them; clean up here if
      // the call never got that far.
      if (stagedPaths.length > 0) {
        await deleteChatwootAttachments(storage, stagedPaths)
      }
      // The draft and the attachments stay put here, so a failed send never
      // silently loses the operator's message.
      if ((error as { name?: string })?.name === 'AbortError') {
        message.info(t('frontend:chatwoot.attachment.sendCanceled'))
      } else {
        errorResponse(
          error,
          getLocalizedErrorMessage(
            error,
            t('frontend:chatwoot.newConversation.failed')
          ),
          { area: 'chatwoot_start_conversation' }
        )
      }
    } finally {
      sendAbortRef.current = null
      setSubmitting(false)
    }
  }

  // Abort an in-flight staging upload. The catch above keeps the draft and the
  // attachments, so cancelling costs the operator nothing.
  function cancelSend() {
    sendAbortRef.current?.abort(new DOMException('Send canceled', 'AbortError'))
  }

  const showDropOverlay = isDraggingOver && !submitting

  return (
    <div
      data-testid="chatwoot-new-conversation-dropzone"
      {...dropzoneProps}
      style={{
        position: 'relative',
        ...(showDropOverlay ? dropzoneHighlightStyle(token) : {}),
      }}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {loadFailed && (
          <Alert
            type="error"
            showIcon
            message={t('frontend:chatwoot.newConversation.loadFailed')}
            action={
              <Button onClick={() => setReloadKey((key) => key + 1)}>
                {t('frontend:chatwoot.action.retry')}
              </Button>
            }
          />
        )}
        {companyResolutionFailed && (
          <Alert
            type="error"
            showIcon
            message={t(
              'frontend:chatwoot.newConversation.resolveCompanyFailed'
            )}
            action={
              <Button onClick={retryCompanyResolution}>
                {t('frontend:chatwoot.action.retry')}
              </Button>
            }
          />
        )}
        <div>
          <Typography.Text
            type="secondary"
            id="cw-new-conversation-inbox-label"
          >
            {t('frontend:chatwoot.field.inbox')}
          </Typography.Text>
          <Select<string>
            style={{ width: '100%', marginTop: 4 }}
            aria-labelledby="cw-new-conversation-inbox-label"
            value={inboxId}
            onChange={setInboxId}
            loading={inboxesStatus === 'loading'}
            placeholder={t('frontend:chatwoot.newConversation.selectInbox')}
            options={inboxes.map((inbox) => ({
              value: inbox.id,
              label: inbox.name || inbox.id,
            }))}
          />
        </div>
        <div>
          <Typography.Text
            type="secondary"
            id="cw-new-conversation-contact-label"
          >
            {t('frontend:chatwoot.field.contact')}
          </Typography.Text>
          <Select<string>
            style={{ width: '100%', marginTop: 4 }}
            aria-labelledby="cw-new-conversation-contact-label"
            showSearch
            // Search is resolved server-side, so don't let antd also filter the
            // already-narrowed result set on the displayed label.
            filterOption={false}
            value={contactId}
            onChange={(value) => {
              setContactId(value)
              setContactName(
                contacts
                  .find((contact) => contact.id === value)
                  ?.name?.trim() || null
              )
              setSelectedCompanyUid(undefined)
              setSearchInput('')
            }}
            onSearch={(value) => {
              setSearchInput(value)
              // A fresh non-empty query means the operator is picking a different
              // recipient; clear any prior selection so Send can't target the old
              // contact instead of the address now being typed.
              if (value.trim() && contactId) {
                setContactId(undefined)
              }
            }}
            loading={contactsStatus === 'loading'}
            placeholder={t(
              'frontend:chatwoot.newConversation.searchContactOrEmail'
            )}
            notFoundContent={
              contactsFailed
                ? t('frontend:chatwoot.newConversation.contactsLoadFailed')
                : contactsStatus === 'loading'
                  ? null
                  : t('frontend:chatwoot.newConversation.noContactsTypeEmail')
            }
            options={contactSelectOptions}
          />
        </div>
        {needsCompanyPick && (
          <div>
            <Typography.Text
              type="secondary"
              id="cw-new-conversation-company-label"
            >
              {t('frontend:chatwoot.newConversation.selectCompany')}
            </Typography.Text>
            <Select<string>
              style={{ width: '100%', marginTop: 4 }}
              aria-labelledby="cw-new-conversation-company-label"
              value={selectedCompanyUid}
              onChange={setSelectedCompanyUid}
              loading={companyResolutionLoading}
              placeholder={t(
                'frontend:chatwoot.newConversation.selectCompanyPlaceholder'
              )}
              options={companyPickerOptions}
            />
          </div>
        )}
        {/* Email-only, and the primary recipient is the contact picked above,
          so only the folded-away Cc and Bcc rows render here. */}
        {isEmailInbox && (
          <ChatwootReplyRecipients
            value={recipients}
            onChange={updateRecipients}
            disabled={submitting}
            showTo={false}
            companyUid={companyUid}
          />
        )}
        {/* Only email threads have a subject line, so the field follows the
          signature's lead and appears for email inboxes only. */}
        {isEmailInbox && (
          <div>
            <Typography.Text
              type="secondary"
              id="cw-new-conversation-subject-label"
            >
              {t('frontend:chatwoot.field.subject')}
            </Typography.Text>
            <Input
              style={{ marginTop: 4 }}
              aria-labelledby="cw-new-conversation-subject-label"
              value={subject}
              onChange={(event) => updateSubject(event.target.value)}
              placeholder={t(
                'frontend:chatwoot.newConversation.subjectPlaceholder'
              )}
            />
          </div>
        )}
        <div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <Typography.Text
              type="secondary"
              id="cw-new-conversation-message-label"
            >
              {t('frontend:chatwoot.newConversation.message')}
            </Typography.Text>
            <Button
              type="link"
              size="small"
              icon={<StarOutlined />}
              disabled={!canCompose}
              title={
                canCompose
                  ? t('frontend:chatwoot.cleo.composeChipHint')
                  : t('frontend:chatwoot.newConversation.composeNeedsRecipient')
              }
              onClick={() => setComposeOpen(true)}
            >
              {t('frontend:chatwoot.cleo.composeChip')}
            </Button>
          </div>
          <TextArea
            style={{ marginTop: 4 }}
            aria-labelledby="cw-new-conversation-message-label"
            value={content}
            onChange={(event) => updateContent(event.target.value)}
            // Cmd/Ctrl+Enter sends; Enter and Shift+Enter start a new line,
            // matching the reply composer and Intercom, so a bare Enter can't
            // fire a mail mid-paragraph. Guarded against sending an incomplete
            // form, an in-flight submit, and an IME composition being confirmed.
            onKeyDown={(event) => {
              if (event.key !== 'Enter') {
                return
              }
              if (!event.ctrlKey && !event.metaKey) {
                return
              }
              if (
                (event.nativeEvent as globalThis.KeyboardEvent).isComposing ||
                !canSubmit ||
                submitting
              ) {
                return
              }
              event.preventDefault()
              void handleSubmit()
            }}
            autoSize={{ minRows: 8, maxRows: 16 }}
            placeholder={t(
              'frontend:chatwoot.newConversation.messagePlaceholder'
            )}
          />
          {isEmailInbox && (
            <Typography.Text
              type="secondary"
              style={{ fontSize: 12, display: 'block', marginTop: 4 }}
            >
              {signatureStatus === 'loading'
                ? t('frontend:chatwoot.hint.signatureLoading')
                : signatureStatus === 'failed'
                  ? t('frontend:chatwoot.hint.signatureUnavailable')
                  : hasSignature(signature)
                    ? t('frontend:chatwoot.hint.signatureAppended')
                    : t('frontend:chatwoot.hint.signatureMissing')}
            </Typography.Text>
          )}
        </div>
        {attachments.length > 0 && (
          <Space size={[8, 8]} wrap style={{ width: '100%' }}>
            {attachments.map((file) => (
              <Space
                key={file.uid}
                size={4}
                style={{
                  border: `1px solid ${token.colorBorderSecondary}`,
                  borderRadius: 6,
                  padding: '2px 6px',
                  maxWidth: 220,
                }}
              >
                <PaperClipOutlined
                  style={{ color: token.colorTextSecondary }}
                />
                <Typography.Text ellipsis style={{ maxWidth: 160 }}>
                  {file.name}
                </Typography.Text>
                <Button
                  type="text"
                  size="small"
                  icon={<CloseOutlined />}
                  aria-label={t('frontend:chatwoot.attachment.remove')}
                  onClick={() => remove(file)}
                />
              </Space>
            ))}
          </Space>
        )}
        {uploadProgress && uploadProgress.totalBytes > 0 && (
          // Determinate progress, so a slow-but-alive upload looks different
          // from a dead one — and the cancel below is a way out that doesn't
          // cost the operator their email.
          <div style={{ width: '100%' }}>
            <Progress
              percent={Math.round(
                (uploadProgress.bytesTransferred / uploadProgress.totalBytes) *
                  100
              )}
              size="small"
              status="active"
            />
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {t('frontend:chatwoot.attachment.uploading', {
                percent: Math.round(
                  (uploadProgress.bytesTransferred /
                    uploadProgress.totalBytes) *
                    100
                ),
              })}
            </Typography.Text>
          </div>
        )}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <Upload
            multiple
            showUploadList={false}
            fileList={attachments}
            beforeUpload={onSelect}
          >
            <Button
              type="text"
              icon={<PaperClipOutlined />}
              disabled={
                submitting || attachments.length >= MAX_OUTBOUND_ATTACHMENTS
              }
            >
              {t('frontend:chatwoot.attachment.attach')}
            </Button>
          </Upload>
          <Space size={8}>
            {/* Only while an upload is actually in flight — see the composer:
                abort reaches the staging step alone. Otherwise keep the plain
                Close, disabled mid-send as before. */}
            {uploadProgress ? (
              <Button onClick={cancelSend}>
                {t('frontend:chatwoot.attachment.cancelUpload')}
              </Button>
            ) : (
              <Button onClick={onClose} disabled={submitting}>
                {t('frontend:common.cancel')}
              </Button>
            )}
            <Button
              type="primary"
              icon={<SendOutlined />}
              loading={submitting}
              disabled={!canSubmit}
              onClick={handleSubmit}
            >
              {t('frontend:chatwoot.newConversation.send')}
            </Button>
          </Space>
        </div>
      </Space>

      <ChatwootCleoComposeModal
        open={composeOpen}
        generating={cleoCompose.status === 'generating'}
        failed={cleoCompose.status === 'error'}
        onSubmit={(instructions) => void onComposeSubmit(instructions)}
        onCancel={() => {
          cleoCompose.reset()
          setComposeOpen(false)
        }}
      />

      {showDropOverlay && <AttachmentDropOverlay />}
    </div>
  )
}
